import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { VbAccountAnnualDetails } from '../../../../../models/transactions/vb-account-annual-details';
import { VbAnnualDetailsProvenanceTypeEnum } from '../../../../../models/enum/shared/vb-annual-details-provenance-type.enum';
import { DateConversionUtils } from '../../../../../utils/date-conversion-utils';
import { DateUtils } from '../../../../../utils/date-utils';

export interface VbPlanDate {
  planYear: number;
  startingMonth: number;
}

// The year the backend reads when it picks an account's band
export function currentVbPlanYear(): number {
  return Number(DateUtils.todayInCST().slice(0, 4));
}

// The backend works new money's limits out from the effective date, not the month payments start
export function vbPlanDateFromEffectiveDate(effectiveDate: string | null): VbPlanDate | null {
  const parts = effectiveDate ? DateConversionUtils.parseDateOnlyParts(effectiveDate) : null;
  return parts ? { planYear: parts.year, startingMonth: parts.month } : null;
}

// The plan year an effective date control picks, falling back to this year until a date is chosen
export function vbPlanYear$(effectiveDate: FormControl<string | null>): Observable<number> {
  return effectiveDate.valueChanges.pipe(
    startWith(effectiveDate.value),
    map(date => vbPlanDateFromEffectiveDate(date)?.planYear ?? currentVbPlanYear())
  );
}

// An account's year is the later of this year and the year it takes effect, so a VB starting next year is
// checked against next year's band
export function vbAccountYear(details: VariableBenefitAccountDetails, thisYear = currentVbPlanYear()): number {
  const effectiveYear = DateConversionUtils.parseDateOnlyParts(details.effectiveDate)?.year ?? thisYear;
  return Math.max(thisYear, effectiveYear);
}

export function findVbBandForYear(details: VariableBenefitAccountDetails, year: number): VbAccountAnnualDetails | null {
  return (
    [details.currentYearAnnualDetails, details.nextYearAnnualDetails].find(band => band?.effectiveYear === year) ?? null
  );
}

export function findVbBandForAccountYear(
  details: VariableBenefitAccountDetails,
  thisYear = currentVbPlanYear()
): VbAccountAnnualDetails | null {
  return findVbBandForYear(details, vbAccountYear(details, thisYear));
}

export function isEstimatedVbBand(band: VbAccountAnnualDetails | null): boolean {
  return band?.provenanceTypeId === VbAnnualDetailsProvenanceTypeEnum.Estimated;
}

export function hasEstimatedVbBandForAccountYear(
  accountDetails: VariableBenefitAccountDetails[],
  thisYear = currentVbPlanYear()
): boolean {
  return accountDetails.some(details => isEstimatedVbBand(findVbBandForAccountYear(details, thisYear)));
}

// Only a future year's limits are estimates
export function isFutureVbPlanYear(year: number, thisYear = currentVbPlanYear()): boolean {
  return year > thisYear;
}
